import { isPlatformBrowser } from '@angular/common';
import { Injectable, OnDestroy, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { Subject } from 'rxjs';
import postRobot from 'post-robot';
import { HostAppContext, HostApplicationName, HostPageNavigatedPayload, HostPlatform } from './host-application.interfaces';

@Injectable({ providedIn: "root" })
export class HostApplicationService implements OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  #context = signal<HostAppContext | undefined>(undefined);
  #token = signal<string | null>(null);

  /**
   * Platform the app is embedded in, derived from the host context.
   *
   * @returns 'on-road' for On-Road, 'on-site' for Vision Pro, otherwise undefined
   * (not embedded, context not yet received, or a host with no Insights platform).
   */
  public readonly computedPlatform = computed<HostPlatform | undefined>(() => {
    switch (this.#context()?.['embedded.aura.unityaiot.com']?.context_host) {
      case HostApplicationName.OnRoad:
        return 'on-road';
      case HostApplicationName.VisionPro:
        return 'on-site';
      default:
        return undefined;
    }
  });

  // events coming from host application
  public onThemeUpdated$: Subject<string> = new Subject<string>();
  public onAuthTokenUpdated$: Subject<string> = new Subject<string>();
  public onContextUpdated$: Subject<HostAppContext> = new Subject<HostAppContext>();
  public onHostPageNavigated$: Subject<HostPageNavigatedPayload> = new Subject<HostPageNavigatedPayload>();

  private readonly secureChannel = {
    window: this.isBrowser ? window.parent : null,
    domain: '*', // TODO: Parent domain can be set so that post-robot only communicates with trusted parent
  };

  // post-robot registers listeners in a global (per-window) registry. Retain the
  // cancel handles so the listeners can be torn down when the service is
  // destroyed, preventing duplicate-registration errors across app lifecycles.
  readonly #listeners: { cancel(): void }[] = [];

  constructor() {
    if (this.isBrowser) {
      this.initParentComms();
    }
  }

  public ngOnDestroy(): void {
    for (const listener of this.#listeners) {
      listener.cancel();
    }
    this.#listeners.length = 0;
  }

  public get isEmbedded(): boolean {
    if (!this.isBrowser) {
      return false;
    }

    return window.self !== window.top;
  }

  private initParentComms() {
    if (!this.secureChannel.window) {
      return;
    }

    this.#listeners.push(
      postRobot.on('changeTheme', this.secureChannel, async (event: any) => {
        console.debug('changeTheme: ', event.data.theme);
        this.onThemeUpdated$.next(event.data.theme);
      }),
    );

    this.#listeners.push(
      postRobot.on('updateApiAuthToken', this.secureChannel, async (event: any) => {
        const token = typeof event.data?.token === 'string' ? event.data.token : null;
        this.#token.set(token);

        if (token) {
          this.onAuthTokenUpdated$.next(token);
        }
      }),
    );

    this.#listeners.push(
      postRobot.on('updateContext', this.secureChannel, async (event: any) => {
        this.applyContext(event.data);
      }),
    );

    /*
      * Notify when host page navigation occurs (e.g., user navigates to a different route/iframe)
    */
    this.#listeners.push(
      postRobot.on('hostPageNavigated', this.secureChannel, async (event: any) => {
        this.onHostPageNavigated$.next(event.data as HostPageNavigatedPayload);
      }),
    );

  }

  public async getCurrentTheme(): Promise<string | null> {
    if (this.isEmbedded && this.secureChannel.window) {
      const res = await postRobot.send(this.secureChannel.window, 'getTheme', {}, { domain: this.secureChannel.domain });
      return typeof res.data === 'string' ? res.data : null;
    }
    return null;
  }

  public async getUserToken(): Promise<string | null> {
    if (!this.isEmbedded || !this.secureChannel.window) {
      console.debug('HostApplicationService.getUserToken called in non-embedded mode');
      this.#token.set(null);
      return null;
    }

    const res = await postRobot.send(this.secureChannel.window, 'getUserToken', {}, { domain: this.secureChannel.domain });
    const token = typeof res.data === 'string' ? res.data : null;
    this.#token.set(token);

    if (token) {
      this.onAuthTokenUpdated$.next(token);
    }

    return token;
  }

  public get getCurrentContext() {
    return this.#context;
  }

  /**
   * Stores the context sent by the host and notifies subscribers of `onContextUpdated$`.
   *
   * @param context - The host context payload; a missing payload is stored as an empty context.
   */
  public applyContext(context: HostAppContext | undefined): void {
    const hostContext = (context || {}) as HostAppContext;
    this.#context.set(hostContext);
    this.onContextUpdated$.next(hostContext);
  }

  public get currentToken() {
    return this.#token;
  }

  /**
   * Get the current token value synchronously (for immediate access without promises).
   * Returns null if no token is available. Use for synchronous token checks.
   */
  public getCurrentTokenValue(): string | null {
    return this.#token();
  }

  public async loadContext() {
    if (this.isEmbedded && this.secureChannel.window) {
      const res = await postRobot.send(this.secureChannel.window, 'getContext', {}, { domain: this.secureChannel.domain });
      this.applyContext(res.data as HostAppContext);
    } else {
      this.#context.set(undefined);
    }
  }

  /**
   * Initialise the host application connection in embedded mode.
   * Requests the auth token and context from the host application.
   * Safe to call multiple times (calls are idempotent after first success).
   */
  public async initialiseEmbeddedMode(): Promise<void> {
    if (!this.isEmbedded) {
      console.debug('HostApplicationService.initialiseEmbeddedMode: Not in embedded mode');
      return;
    }

    try {
      // Request token and context from host application
      await Promise.all([this.getUserToken(), this.loadContext()]);
      console.debug('HostApplicationService: Embedded mode initialised with host token');
    } catch (error) {
      console.warn('HostApplicationService: Failed to initialise embedded mode', error);
    }
  }
}